"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./DealsListNew.module.css";
import BookDealsCardNew from "./BookDealsCardNew";
import { transformBookDealsData } from "./dealsHelper";
import { apiRequestWithRetry } from "src/services/ClientSideAPIService";
import { useAppContext } from "@components/Provider";
import Image from "next/image";
import { convertToQueryString, fireGoogleTags } from "helpers/Helper";

const DealsListNew = ({
  deals,
  postType,
  totalDeals,
  totalComponents,
  totalPages,
  selectedFilters,
  bannerInfo,
  loading,
  setLoading,
  slug: propSlug, // Add slug as prop
}) => {
  //const [loading, setLoading] = useState(true);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const [page, setPage] = useState(2);
  const [dealList, setDealList] = useState(deals || []);
  const [totalPagesCount, setTotalPagesCount] = useState(totalPages || 1);
  const [totalDealsCount, setTotalDealsCount] = useState(
    totalDeals || totalComponents || 0
  );
  const pageSize = 20;

  const requestInProgressRef = useRef(false);
  const hasLoadedMoreRef = useRef(false); // Track if we've loaded more deals internally
  const {
    data,
    siteId,
    router,
    subscriberCookie,
    isUserComingFromNL,
    isPreview,
  } = useAppContext();
  // Use prop slug if available, otherwise extract from router
  const slug = propSlug || (router.asPath && router.asPath?.split("/").pop());
  const { SiteSettings } = data;
  const options = {
    Newsletter_Settings: {
      ...SiteSettings.newsletter_settings,
    },
    postType,
    pageSize,
    lastPageNumber: totalPagesCount,
    currentPageNumber: page,
    skip: page > 0 && (page - 1) * pageSize,
  };

  let shouldDisplayNL = false;

  if (!subscriberCookie && !isUserComingFromNL) shouldDisplayNL = true;
  if (
    isPreview ||
    //(props && props.isGiveaway) ||
    options.homePage ||
    options.categoryFunnel ||
    options.funnelPage
  )
    shouldDisplayNL = true;

  // Sync deals from props only when deals prop changes AND we haven't loaded more internally
  // Once we start loading more, we become the source of truth and ignore prop updates
  useEffect(() => {
    // Only sync if we haven't loaded more deals internally AND we're not currently loading
    if (!hasLoadedMoreRef.current && !requestInProgressRef.current) {
      setDealList(deals || []);
      setTotalDealsCount(totalDeals || totalComponents || 0);
      setLoading(false);
    }
  }, [deals]);
  
  // Handle totalDeals separately to avoid interfering with dealList when we've loaded more
  useEffect(() => {
    if (!hasLoadedMoreRef.current) {
      setTotalDealsCount(totalDeals || totalComponents || 0);
    }
  }, [totalDeals, totalComponents]);

  useEffect(() => {
    if (
      selectedFilters !== undefined &&
      Object.keys(selectedFilters).length > 0
    ) {
      setPage(1); // Reset page to 1
      setDealList([]); // Clear existing deals
      setTotalDealsCount(totalDeals || totalComponents || 0);
      requestInProgressRef.current = false;
      hasLoadedMoreRef.current = false; // Reset flag when filters change to allow syncing from props again
    }
  }, [selectedFilters, totalDeals, totalComponents]);

  // Trigger fetchDeals only after page is updated to 1
  useEffect(() => {
    if (
      page === 1 &&
      selectedFilters !== undefined &&
      Object.keys(selectedFilters).length > 0
    ) {
      setLoading(false);
      fetchDeals(selectedFilters);
    }
  }, [page, selectedFilters]);

  useEffect(() => {
    if (totalPages) {
      setTotalPagesCount(totalPages);
    }
  }, [totalPages]);

  /**
   * Fetch next page of deals when scrolling.
   * ✅ Prevents duplicate calls.
   * ✅ Correctly resets request flag.
   */
  const fetchDeals = useCallback(
    async (selectedFilters) => {
      if (requestInProgressRef.current) return;

      if (page > totalPagesCount) return;

      requestInProgressRef.current = true;
      setIsLoadMore(page > 1); // ✅ Set loader state before making API call
      setLoading(page === 1);
      try {
        // Check if slug already starts with "deals/" to avoid duplication
        const finalSlug = slug?.startsWith("deals/") ? slug : `deals/${slug}`;
        let filter = {
          siteid: siteId,
          slug: finalSlug,
          searchTerm: selectedFilters?.searchTerm?.trim(),
        };

        // Add filters only if they have values
        const hasGenres = selectedFilters.genres?.length > 0;
        const hasRetailers = selectedFilters.retailers?.length > 0;
        const hasAgeRange = selectedFilters.ageRanges?.length > 0;
        const hasPrice = selectedFilters.maxPrice !== 5;
        const hasRating = selectedFilters.rating > 0;
        const hasIsbn = selectedFilters.isbn || "";

        if (hasGenres) filter.genres = selectedFilters.genres;
        if (hasRetailers)
          filter.retailers = selectedFilters.retailers.join(",");
        if (hasAgeRange) filter.ageRanges = selectedFilters.ageRanges.join(",");
        if (hasPrice) {
          filter.minPrice = selectedFilters.minPrice;
          filter.maxPrice = selectedFilters.maxPrice;
        }
        if (hasRating) filter.rating = selectedFilters.rating;
        if (hasIsbn) filter.isbn = selectedFilters.isbn;

        let reqBody = {
          ...filter,
          page,
          pageSize: 20,
        };

        let url;
        let requestOptions;

        // isUserAppliedFilter will be true when user is coming from NL
        if (page === 1 && !selectedFilters.isUserAppliedFilter) {
          const queryString = convertToQueryString(reqBody);
          // Slugify
          const slugifyslug = finalSlug.replace("/", "_");
          // First load → GET route
          url = `/template/deals/filter/${slugifyslug}/${siteId}?${queryString}`;
          requestOptions = { method: "GET" };
        } else {
          // Subsequent pages → POST route
          // POST route only needs siteId, slug is in the request body
          url = `/template/deals/filter/${siteId}`;
          requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(reqBody),
          };
        }

        const response = await apiRequestWithRetry(url, requestOptions);

        const newDeals = response?.post?.components || [];
        const totalPagesFromApi = response?.totalPages || 1;
        const totalItems = response?.totalItems || 0;

        if (newDeals.length === 0) return;

        const formattedNewDeals = await transformBookDealsData(newDeals);
        
        // Deduplicate: filter out deals that already exist in the list
        // Use both id and isbn as unique identifiers
        setDealList((prev) => {
          const existingIds = new Set(prev.map(deal => deal.id).filter(Boolean));
          const existingIsbns = new Set(prev.map(deal => deal.isbn).filter(isbn => isbn && isbn !== "N/A"));
          const uniqueNewDeals = formattedNewDeals.filter(deal => {
            // Skip if deal has same id or same isbn as existing deals
            if (deal.id && existingIds.has(deal.id)) return false;
            if (deal.isbn && deal.isbn !== "N/A" && existingIsbns.has(deal.isbn)) return false;
            return true;
          });
          return [...prev, ...uniqueNewDeals];
        });
        
        setPage((prevPage) => prevPage + 1);
        setTotalPagesCount(totalPagesFromApi);
        hasLoadedMoreRef.current = true; // Mark that we've loaded more deals
        fireGoogleTags();
        if (selectedFilters?.searchTerm?.trim()) {
          fireBookSearchGTMEvent(
            selectedFilters.searchTerm.trim().toLowerCase(),
            totalItems
          );
        }
      } catch (error) {
        console.error("🚨 API Error:", error);
      } finally {
        requestInProgressRef.current = false;
        setIsLoadMore(false); // ✅ Hide loader when response arrives
        setLoading(false);
      }
    },
    [slug, page, totalPagesCount]
  );

  const fireBookSearchGTMEvent = (searchTerm, searchResultsCount) => {
    if (typeof window !== "undefined") {
      if (searchTerm) {
        const eventObj = {
          event: "BookSearch",
          searchTerm,
          searchResultsCount,
        };
        window.dataLayer.push(eventObj);
      }
    }
  };

  /**
   * Scroll event listener to trigger fetching new deals.
   * ✅ Uses debounced check to avoid rapid multiple calls.
   */
  const handleScroll = useCallback(() => {
    if (requestInProgressRef.current) return;

    const nearBottom =
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight - 400; // ✅ Adjust threshold

    if (nearBottom && !loading && !isLoadMore) {
      fetchDeals(selectedFilters);
    }
  }, [fetchDeals]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]); // ✅ Stable dependency list

  // const memoizedDeals = useMemo(() => dealList, [dealList]);

  return (
    <section className={styles.bookCardBox}>
      {loading ? (
        <>
          <div className={styles["dealspage__loader"]}>
            Loading...Please Wait
          </div>
        </>
      ) : (
        <>
          {dealList.length > 0 ? (
            dealList.map((deal, index) => {
              if (deal.type === "book-listicle-info") {
                return (
                  <BookDealsCardNew
                    key={deal.id || index}
                    deal={deal}
                    index={index}
                    bannerInfo={bannerInfo}
                    selectedRetailers={selectedFilters.retailers}
                    options={options}
                    shouldDisplayNL={shouldDisplayNL}
                    postType={postType}
                  />
                );
              }
            })
          ) : (
            <div className={styles["dealspage__loader"]}>No Deals Found</div>
          )}

          {/* Loader at bottom when fetching new deals */}
          {isLoadMore && (
            <div className={styles.loadMoreText}>Loading more deals...</div>
          )}
        </>
      )}
    </section>
  );
};

export default DealsListNew;